/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ENTER} from '@angular/cdk/keycodes';
import {TownshipService} from '../../../_services/township.service';
import {Township} from '../../../_models/township';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {AND, EQ, STARTS_WITH} from '../../../_enums/operators';
import {ID, ZIP_MAX_LENGTH, ZIP_MIN_LENGTH} from '../../../_enums/constants';
import {isControlInvalid, isControlValid, TOUCHED_OR_SUBMITTED} from '../../../_enums/form-validation';
import {debounceTime, finalize} from 'rxjs/operators';
import {tap} from 'rxjs/internal/operators/tap';
import {switchMap} from 'rxjs/internal/operators/switchMap';
import {Helpers} from '../../../helpers';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {I18nService} from '../../../_services/i18n.service';
import {ToastrService} from 'ngx-toastr';



@Component({
  selector: 'app-input-township',
  templateUrl: './input-township.component.html',
  styleUrls: ['./input-township.component.scss']
})
export class InputTownshipComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             BASE COMPONENT INPUTS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() controlName?: string;
  @Input() disableEnterKeyPress?: boolean;
  @Input() formGroup?: FormGroup;
  @Input() index?: number;
  @Input() label?: string;
  @Input() readonly ?: boolean;
  @Input() showLabel?: boolean;
  @Input() showRequired?: boolean;
  @Input() showValidation?: string;
  @Input() submitted?: boolean;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             CUSTOM COMPONENT INPUTS                                                                                ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() labelTownshipName?: string;
  @Input() labelZip?: string;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             COMPONENT OUTPUTS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Output() updated = new EventEmitter<string>();

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public townships: Township[] = [];
  public zipControlName = 'zip';
  public townshipNameControlName = 'townshipName';

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(private i18n: I18nService, private toastr: ToastrService, private townshipService: TownshipService) {
    this.controlName = 'township';
    this.disableEnterKeyPress = false;
    this.formGroup = null;
    this.index = 0;
    this.label = null;
    this.readonly = false;
    this.showLabel = true;
    this.showRequired = true;
    this.showValidation = TOUCHED_OR_SUBMITTED;
    this.submitted = false;

    this.labelTownshipName = null;
    this.labelZip = null;
  }

  ngOnInit() {
    if (this.formGroup) {
      if (!this.formGroup.contains(this.zipControlName)) {
        this.formGroup.addControl(this.zipControlName, new FormControl(null));
      }

      if (!this.formGroup.contains(this.townshipNameControlName)) {
        this.formGroup.addControl(this.townshipNameControlName, new FormControl(null));
      }
    }

    if (this.zipControl) {
      this.zipControl.setValidators(null);
      this.zipControl.setValidators([Validators.maxLength(ZIP_MAX_LENGTH), Validators.minLength(ZIP_MIN_LENGTH), Validators.required]);
      this.zipControl.updateValueAndValidity();

      this.zipControl.valueChanges.pipe(
        debounceTime(300),
        tap(() => Helpers.setLoading(true)),
        switchMap(value => this.townshipService.find({
            filter: [
              ['zip', STARTS_WITH, String(value)], AND,
              ['collectable', EQ, true]
            ]
          }).pipe(finalize(() => Helpers.setLoading(false)))
        )).subscribe((res: HttpResponse<Township[]>) => this.townships = res.body, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORM CONTROLS GETTERS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get control(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.controlName) : null;
  }

  get townshipNameControl(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.townshipNameControlName) : null;
  }

  get zipControl(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.zipControlName) : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get controlId(): string | null {
    return `${this.controlName}_${this.index}_${ID}`;
  }

  public displayZip(t?: Township): string {
    return t && t.zip ? t.zip : String(t);
  }

  get required(): boolean {
    return this.validators && this.validators.hasOwnProperty('required');
  }

  get showInvalid(): boolean {
    return isControlInvalid(this.control, this.showValidation, this.submitted);
  }

  get showValid(): boolean {
    return isControlValid(this.control, this.showValidation);
  }

  get townshipNameControlId(): string {
    return `${this.townshipNameControlName}_${this.index}_${ID}`;
  }

  get validators(): ValidationErrors | null {
    return this.control && this.control.validator && this.control.validator(this.control);
  }

  get zipControlId(): string {
    return `${this.zipControlName}_${this.index}_${ID}`;
  }

  get zipShowInvalid(): boolean {
    return isControlInvalid(this.zipControl, this.showValidation, this.submitted);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              CHANGE LISTENERS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public onChange(e?: MatAutocompleteSelectedEvent): void {
    if (e && e.option && e.option.value) {
      this.control.setValue(e.option.value.id);
      this.zipControl.setValue(e.option.value.zip);
      this.townshipNameControl.setValue(e.option.value.name);
      this.updated.emit(e.option.value);
    }
  }

  public keypress(e): void {
    if (e.keyCode === ENTER && this.disableEnterKeyPress) {
      e.preventDefault();
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

}
